import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';

const NOW_PLAYING_URL = 'https://radio.serrebiradio.com/api/nowplaying/serrebiradio';
const STREAM_MP3_URL = 'https://radio.serrebiradio.com/listen/serrebiradio/SerrebiRadio';
const STREAM_OPUS_URL = 'https://radio.serrebiradio.com/listen/serrebiradio/SerrebiRadioHD';
const REQUEST_URL = 'https://radio.serrebiradio.com/public/serrebiradio';
const PERSONAL_MASTODON_URL = 'https://tweesecake.social/@serrebi';
const PROJECTS_URL = 'https://projects.serrebiradio.com/';
const NOW_PLAYING_REFRESH_MS = 30000;

const panelStyle = {
  marginBottom: '16px',
  padding: '14px',
  borderRadius: '12px',
  border: '1px solid var(--background-border-color)',
  background: 'var(--background-color)',
};

const headingStyle = {
  margin: '0 0 8px',
  fontSize: '18px',
  lineHeight: 1.35,
  color: 'var(--primary-text-color)',
};

const bodyTextStyle = {
  margin: '0 0 10px',
  color: 'var(--secondary-text-color)',
  lineHeight: 1.5,
};

const nowPlayingRowStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '4px',
  alignItems: 'baseline',
  margin: '0 0 10px',
  color: 'var(--primary-text-color)',
  lineHeight: 1.4,
};

const nowPlayingLabelStyle = {
  fontWeight: 700,
  marginRight: '4px',
};

const nowPlayingValueStyle = {
  fontWeight: 500,
};

const audioStyle = {
  width: '100%',
};

const linkListStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
  gap: '8px',
  margin: '12px 0 0',
  padding: 0,
  listStyle: 'none',
};

const linkStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '38px',
  width: '100%',
  boxSizing: 'border-box',
  padding: '7px 10px',
  borderRadius: '999px',
  border: '1px solid var(--background-border-color)',
  background: 'var(--elevated-background-color)',
  color: 'var(--primary-text-color)',
  textDecoration: 'none',
  fontSize: '13px',
  fontWeight: 600,
};

const srOnlyStyle = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  border: 0,
  whiteSpace: 'nowrap',
};

const normalizeNowPlaying = payload => {
  const song = payload?.now_playing?.song;

  if (!song) {
    return null;
  }

  const artist = song.artist?.trim();
  const title = (song.title || song.text || '').trim();

  if (!artist && !title) {
    return null;
  }

  if (artist && title) {
    return `${artist} - ${title}`;
  }

  return artist || title;
};

export const SerrebiRadioPanel = ({ showHeader = true }) => {
  const [nowPlaying, setNowPlaying] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isCancelled = false;

    const fetchNowPlaying = async () => {
      try {
        const response = await fetch(NOW_PLAYING_URL, { method: 'GET' });

        if (!response.ok) {
          throw new Error(`Failed to fetch now playing (${response.status})`);
        }

        const payload = await response.json();

        if (!isCancelled) {
          setNowPlaying(normalizeNowPlaying(payload));
        }
      } catch {
        if (!isCancelled) {
          setNowPlaying(null);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchNowPlaying();
    const timerId = window.setInterval(fetchNowPlaying, NOW_PLAYING_REFRESH_MS);

    return () => {
      isCancelled = true;
      window.clearInterval(timerId);
    };
  }, []);

  const nowPlayingText = useMemo(() => {
    if (isLoading) {
      return 'Loading now playing...';
    }

    if (nowPlaying) {
      return nowPlaying;
    }

    return 'Currently unavailable';
  }, [isLoading, nowPlaying]);

  return (
    <section className='serrebi-radio-panel' style={panelStyle}>
      {showHeader ? (
        <h3 style={headingStyle}>Serrebi Radio</h3>
      ) : (
        <h3 style={srOnlyStyle}>Serrebi Radio</h3>
      )}

      <p style={bodyTextStyle}>This Mastodon is just for Serrebi and friends. Registration is closed.</p>

      <p style={nowPlayingRowStyle} aria-live='polite' aria-atomic='true'>
        <span style={nowPlayingLabelStyle}>Now playing:</span>
        <span style={nowPlayingValueStyle}>{nowPlayingText}</span>
      </p>

      <audio controls preload='none' src={STREAM_MP3_URL} style={audioStyle} aria-label='Serrebi Radio MP3 stream player'>
        Your browser does not support the audio player.
      </audio>

      <ul style={linkListStyle} aria-label='Serrebi Radio links'>
        <li>
          <a href='https://radio.serrebiradio.com/' target='_blank' rel='noopener noreferrer' style={linkStyle}>
            Internet Radio
            <span style={srOnlyStyle}> (opens in a new tab)</span>
          </a>
        </li>

        <li>
          <a href={PROJECTS_URL} target='_blank' rel='noopener noreferrer' style={linkStyle}>
            My Projects
            <span style={srOnlyStyle}> (opens in a new tab)</span>
          </a>
        </li>

        <li>
          <a href={STREAM_MP3_URL} style={linkStyle}>listen in mp3</a>
        </li>

        <li>
          <a href={STREAM_OPUS_URL} style={linkStyle}>listen in Opus</a>
        </li>

        <li>
          <a href={REQUEST_URL} target='_blank' rel='noopener noreferrer' style={linkStyle}>
            request a song
            <span style={srOnlyStyle}> (opens in a new tab)</span>
          </a>
        </li>

        <li>
          <a href={PERSONAL_MASTODON_URL} target='_blank' rel='noopener noreferrer' style={linkStyle}>
            follow me on Mastodon!
            <span style={srOnlyStyle}> (opens in a new tab)</span>
          </a>
        </li>
      </ul>
    </section>
  );
};

SerrebiRadioPanel.propTypes = {
  showHeader: PropTypes.bool,
};
